import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const editable = user?.role === "super_admin";

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/roles");
      setRoles(data.roles || []);
      setPermissions(data.permissions || []);
      setMatrix(data.matrix || {});
    })();
  }, []);

  const has = (roleSlug, perm) => {
    return (matrix[roleSlug] || []).includes(perm);
  };
  const toggle = (roleSlug, permission) => {
    if (!editable || roleSlug === "super_admin") return;
    setMatrix((current) => {
      const currentPermissions = current[roleSlug] || [];
      const next = currentPermissions.includes(permission) ? currentPermissions.filter((item) => item !== permission) : [...currentPermissions, permission];
      return { ...current, [roleSlug]: next };
    });
  };
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.patch("/roles/permissions", { matrix }); setMatrix(data.matrix || matrix); toast.success("Permissions saved and enforced"); }
    catch (error) { toast.error(error.response?.data?.detail || "Could not save permissions"); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <PageHeader
        title="Roles & Permissions"
        subtitle={editable ? "Select access for each role. Changes take effect immediately across protected modules." : "View the active role permissions. Only a super admin can make changes."}
        actions={editable ? <Button onClick={save} disabled={saving} className="btn-gradient text-white border-0"><Icons.Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save permissions"}</Button> : null}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {roles.map((r) => (
          <GlassCard key={r.slug} className="p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg btn-gradient flex items-center justify-center">
                <Icons.ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-white font-medium">{r.name}</div>
                <div className="text-xs text-white/50 mt-0.5">{r.description}</div>
                <div className="text-[10px] text-white/40 mt-1 font-mono">{r.slug}</div>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      <GlassCard className="p-0">
        <div className="px-6 py-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-medium text-white">Permission Matrix</h3>
          <p className="text-xs text-white/40 mt-0.5">{editable ? "Select a box to grant or remove module access. Super Admin always has full access." : "Permission changes are restricted to a super admin."}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left uppercase tracking-wider text-white/45 bg-white/[0.02]">
                <th className="px-4 py-3 font-medium sticky left-0 bg-[#141428]">Module</th>
                {roles.map((r) => (
                  <th key={r.slug} className="px-2 py-3 font-medium text-center whitespace-nowrap">{r.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {permissions.map((p) => (
                <tr key={p} className="border-t border-white/5">
                  <td className="px-4 py-2.5 text-white/80 capitalize sticky left-0 bg-[#141428]">{p.replace("_", " ")}</td>
                  {roles.map((r) => (
                    <td key={r.slug} className="px-2 py-2.5 text-center">
                      <div className="flex justify-center">
                        <Checkbox checked={has(r.slug, p)} disabled={!editable || r.slug === "super_admin"} onCheckedChange={() => toggle(r.slug, p)} className="data-[state=checked]:bg-[#8B5CF6] data-[state=checked]:border-[#8B5CF6]" />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  );
}
